// 花嫁「破れたバージンロード」(thornAisle・kind 'thorns') の床の結晶の描き方 (2026-10-10・ユーザー採用で既定は early)。
//
// 出所 = docs/research/2026-10-10-knight-bride-fresh-eyes.md の B1。
//   溜めの 1 秒は濃い紫の結晶 (poison_mist) が高さ 140px で立っている。当たりの最初のコマで溜めの絵が消え、
//   当たりの絵 (thorn_growth) は高さ 0 から 90ms かけて伸びる。だから当たる瞬間は床の穴しか見えず、そこで被弾する。
//   状況と動画 = docs/research/2026-10-10-thorn-rise-status.md
//
// 開発版の切り替え ?thornRise=off|keep|early (読むのは下の DEV の箱の中だけ)。指定なし／他の値＝既定の early。配布版は常に early。
//   off   = 旧 (2026-10-10 より前の配布版)。DEV で ?thornRise=off (または now / old) を付けると見比べられる。
//   keep  = 当たりの絵が伸びきるまで (90ms) 溜めの結晶を残して重ね、伸びるのに合わせて消す。
//   early = 既定 (2026-10-10 ユーザー採用)。溜めの終わり 90ms で当たりの絵を先に伸ばし始め、当たりの開始で伸びきっている。
// 🔴 見た目だけ。当たりの時間・範囲・ダメージはどれでも同じ (hits.ts は触らない)。
//   ⚠️ 識別子に URL のキーの綴りを入れない (scripts/check_dev_leak.mjs が配布ビルドの文字列を見張る)。

import { brideThornMotif, drawRoseAisle } from './bride_rose_thorns'

export type BrideThornLook = 'off' | 'keep' | 'early'
export const BRIDE_THORN_LOOKS: readonly BrideThornLook[] = ['off', 'keep', 'early']
export function parseBrideThornLook(v: unknown): BrideThornLook | null {
  const k = typeof v === 'string' ? v.toLowerCase() : ''
  if (k === 'now' || k === 'old') return 'off'
  return (BRIDE_THORN_LOOKS as readonly string[]).includes(k) ? k as BrideThornLook : null
}
let look: BrideThornLook = 'early'
export function brideThornLook(): BrideThornLook { return look }
/** テスト用。配布版では URL を読まないので、ここを呼ぶ物はテストだけ。 */
export function setBrideThornLookForTest(next: BrideThornLook): void { look = next }

/** 当たりの絵が伸びきるまでの時間 (ms)。今の render.ts の rise と同じ。 */
export const THORN_GROW_MS = 90
/** 溜めの結晶 (poison_mist) の描く高さ */
export const THORN_MIST_H = 140
/** 当たりの結晶 (thorn_growth) の伸びきった高さ */
export const THORN_GROWTH_H = 180

export interface ThornAisleFrame {
  /** 溜めの結晶の不透明度。0 = 描かない */
  mistAlpha: number
  /** 当たりの結晶の伸び 0..1。0 = 描かない */
  growth: number
  /** 当たりの結晶の不透明度 */
  growthAlpha: number
}

/** 溜めの終盤の脈動 (今の render.ts と同じ式)。nowMs = performance.now() */
function latePulse(nowMs: number): number {
  return 1 + 0.18 * Math.sin(nowMs / 1000 * 14)
}

/**
 * 帯1本のいまの描き方。elapsedMs = 帯の経過 (溜めを含む)。off は旧の render.ts の式そのまま。
 */
export function thornAisleFrame(elapsedMs: number, telegraphMs: number, activeMs: number, nowMs: number,
  m: BrideThornLook = look): ThornAisleFrame {
  if (elapsedMs < telegraphMs) {
    const progress = elapsedMs / telegraphMs
    const pulse = progress >= 0.7 ? latePulse(nowMs) : 1
    const mistAlpha = Math.max(0, Math.min(1, Math.min(1, 0.4 + 1.5 * progress) * pulse))
    // early: 溜めの最後 90ms で当たりの絵を伸ばし始める (当たりの開始で伸びきる)。
    const growth = m === 'early' ? Math.max(0, Math.min(1, (elapsedMs - (telegraphMs - THORN_GROW_MS)) / THORN_GROW_MS)) : 0
    return { mistAlpha, growth, growthAlpha: 1 }
  }
  const activeElapsed = elapsedMs - telegraphMs
  const aProg = Math.min(1, activeElapsed / activeMs)
  const rise = Math.min(1, activeElapsed / THORN_GROW_MS)
  const fade = aProg > 0.85 ? (1 - aProg) / 0.15 : 1
  if (m === 'early') return { mistAlpha: 0, growth: 1, growthAlpha: fade }
  if (m === 'keep' && rise < 1) {
    // 溜めの終わりの濃さから、当たりの絵が伸びるのに合わせて消す。前半は残し、伸びきる手前で抜ける (1 − rise²)。
    const endAlpha = Math.max(0, Math.min(1, latePulse(nowMs)))
    return { mistAlpha: endAlpha * (1 - rise * rise), growth: rise, growthAlpha: fade }
  }
  return { mistAlpha: 0, growth: rise, growthAlpha: fade }
}

type HazardImage = CanvasImageSource & { naturalWidth: number; naturalHeight: number }
type ThornCtx = Pick<CanvasRenderingContext2D, 'save' | 'restore' | 'drawImage' | 'globalAlpha' | 'globalCompositeOperation'>

/** 溜めの結晶 (poison_mist) を床から 140px で描く。crop = 素材の両端の捨てる割合。 */
export function drawThornMist(ctx: ThornCtx, img: HazardImage, xStart: number, w: number, floorY: number,
  alpha: number, crop: number): void {
  const srcX = img.naturalWidth * crop
  const srcW = img.naturalWidth * (1 - crop * 2)
  ctx.save()
  ctx.globalCompositeOperation = 'source-over'
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha))
  ctx.drawImage(img, srcX, 0, srcW, img.naturalHeight, xStart, floorY - THORN_MIST_H, w, THORN_MIST_H)
  ctx.restore()
}

/** 当たりの結晶 (thorn_growth) を、素材の下から growth の割合だけ見せて床から伸ばす。 */
export function drawThornGrowth(ctx: ThornCtx, img: HazardImage, xStart: number, w: number, floorY: number,
  growth: number, alpha: number, crop: number): void {
  const dhCur = THORN_GROWTH_H * growth
  const srcH = img.naturalHeight
  const srcHShown = srcH * growth
  if (!(srcHShown > 0)) return
  const srcX = img.naturalWidth * crop
  const srcW = img.naturalWidth * (1 - crop * 2)
  ctx.save()
  ctx.globalCompositeOperation = 'source-over'
  ctx.globalAlpha = alpha
  ctx.drawImage(img, srcX, srcH * (1 - growth), srcW, srcHShown, xStart, floorY - dhCur, w, dhCur)
  ctx.restore()
}

/**
 * 帯1本を描く (溜めの結晶 → 当たりの結晶の順)。mist / growth の絵が無い層は描かない。
 *   render.ts の drawHazards が kind 'thorns' で呼ぶ。off は旧と同じ絵になる。
 */
export function drawThornAisle(ctx: ThornCtx, hz: { elapsedMs: number; telegraphMs: number; activeMs: number; xStart: number },
  w: number, floorY: number, nowMs: number,
  img: { mist: HazardImage | null; growth: HazardImage | null }, crop: { mist: number; growth: number },
  m: BrideThornLook = look): ThornAisleFrame {
  // 薔薇の茨 (既定・DEV の ?thornLook=now で旧の結晶・src/bride_rose_thorns.ts)。部品の絵が未読込みの間は下の結晶で描く。
  if (brideThornMotif() === 'rose' && drawRoseAisle(ctx as CanvasRenderingContext2D, hz, w, floorY, nowMs)) {
    return { mistAlpha: 0, growth: hz.elapsedMs >= hz.telegraphMs ? 1 : 0, growthAlpha: 1 }
  }
  const f = thornAisleFrame(hz.elapsedMs, hz.telegraphMs, hz.activeMs, nowMs, m)
  if (img.mist && f.mistAlpha > 0) drawThornMist(ctx, img.mist, hz.xStart, w, floorY, f.mistAlpha, crop.mist)
  if (img.growth && f.growth > 0) drawThornGrowth(ctx, img.growth, hz.xStart, w, floorY, f.growth, f.growthAlpha, crop.growth)
  return f
}

// URL の読み取りは最後 (上の let が初期化された後)。配布版はこの箱ごと消える = 常に early。
if (import.meta.env.DEV && typeof window !== 'undefined') {
  look = parseBrideThornLook(new URLSearchParams(window.location.search).get('thornRise')) ?? 'early'
}
